import { auroraBaseColors, auroraLightThemeColors, AuroraThemeColors } from './auroraColors'
import { baseColors } from './colors'

export const bendBaseColors = {
  ...auroraBaseColors,

  // Blue
  blue_10: '#F1F2F9',
  blue_20: '#DBDFF1',
  blue_30: '#A0AADD',
  blue_40: '#6471B4',
  blue_50: '#47528E',
  blue_60: '#384275',

  // Gray
  gray_10: '#F2F0EE',
  gray_20: '#D3D2CE',
  gray_30: '#97948D',
  gray_40: '#787672',
  gray_50: '#5A5955',
  gray_60: '#292927',

  // Green
  green_10: '#ECFAF1',
  green_20: '#D0F3DC',
  green_30: '#80D49D',
  green_40: '#39BC66',
  green_50: '#018847',
  green_60: '#016C39',

  // Orange
  orange_10: '#FFF6ED',
  orange_20: '#FFE8D2',
  orange_30: '#FFC8A0',
  orange_40: '#FFA578',
  orange_50: '#CA6438',
  orange_60: '#AA3A0A',

  // Purple
  purple_10: '#F6EEFA',
  purple_20: '#E9D4F2',
  purple_30: '#D7A2DF',
  purple_40: '#B666B6',
  purple_50: '#944594',
  purple_60: '#6C2D6C',

  // Red
  red_10: baseColors.red_10,
  red_20: '#FFDADA',
  red_30: '#FFAFB4',
  red_40: '#FF7291',
  red_50: '#C34762',
  red_60: '#96233C',

  // Soft green
  soft_green_10: '#F1FAEC',
  soft_green_20: '#DEF5D3',
  soft_green_30: '#C1E3AF',
  soft_green_40: '#A0D088',
  soft_green_50: '#63A344',
  soft_green_60: '#3E8704',

  // Teal
  teal_10: '#EDF9F9',
  teal_20: '#D3F0F0',
  teal_30: '#6FC5C5',
  teal_40: '#089C9C',
  teal_50: '#078189',
  teal_60: '#026572',

  // Yellow
  yellow_10: '#FDFBEB',
  yellow_20: '#FCF8D1',
  yellow_30: '#F6EE9E',
  yellow_40: '#EED65B',
  yellow_50: '#D4A600',
  yellow_60: '#BD8902',

  error: '#ED0303',
  scrim: '#1A1918CC',
  gray_10_opacity_60: '#F2F0EE99',

  // Bend-local legacy and infographic colors.
  legacy_teal_40: '#017A8D',
  infographic_cobalt: auroraBaseColors.cobalt_60,
  infographic_green: '#78A842',
  infographic_orange: baseColors.gold_40,
  infographic_pink: '#E06C9C',
  infographic_purple: '#7D8FE3',
  infographic_teal: '#39A0B0',
}

export const bendLightThemeColors: AuroraThemeColors = {
  ...auroraLightThemeColors,

  // Text
  textPrimary: bendBaseColors.gray_60,
  textSecondary: bendBaseColors.gray_60,
  textInactive: bendBaseColors.gray_30,
  textActive: bendBaseColors.green_50,
  textSuccess: bendBaseColors.green_60,
  textSuccessAlternate: bendBaseColors.green_60,
  textWarning: bendBaseColors.orange_60,
  textError: bendBaseColors.error,
  textButton: baseColors.white,
  textInverse: baseColors.white,

  // Link
  linkDefault: bendBaseColors.green_50,
  linkHover: bendBaseColors.green_60,
  linkInactive: bendBaseColors.gray_40,

  // Background
  backgroundPrimary: baseColors.white,
  backgroundSecondary: baseColors.white,
  backgroundSection: baseColors.white,
  backgroundHighlightPrimary: bendBaseColors.teal_10,
  backgroundHighlightSecondary: bendBaseColors.green_10,
  backgroundHighlightTertiary: bendBaseColors.purple_10,
  backgroundTransparent: baseColors.transparent,
  backgroundActive: bendBaseColors.green_50,
  backgroundHover: bendBaseColors.green_60,
  backgroundPressed: bendBaseColors.green_60,
  backgroundLoading: bendBaseColors.green_60,
  backgroundFocus: bendBaseColors.gray_10,
  backgroundInactive: bendBaseColors.gray_10,
  backgroundSuccess: bendBaseColors.soft_green_10,
  backgroundWarning: bendBaseColors.orange_10,
  backgroundError: baseColors.red_10,
  backgroundTooltip: bendBaseColors.teal_60,
  backgroundScrim: bendBaseColors.scrim,
  backgroundElement: baseColors.white,
  backgroundHeaderPeriwinkle: bendBaseColors.purple_10,
  backgroundHeaderPrimary: bendBaseColors.teal_10,
  backgroundHeaderSecondary: bendBaseColors.purple_10,
  backgroundNavHeader: baseColors.white,
  backgroundPhoto: bendBaseColors.teal_60,
  backgroundModal: baseColors.white,
  backgroundGlassmorphicTabBar: baseColors.white,
  backgroundSurfaceElement: baseColors.white,
  backgroundDropdown: baseColors.white,

  // Border
  borderDefault: bendBaseColors.gray_20,
  borderActive: bendBaseColors.gray_60,
  borderHover: bendBaseColors.gray_60,
  borderFocus: bendBaseColors.gray_60,
  borderInput: bendBaseColors.gray_20,
  borderSuccess: bendBaseColors.green_50,
  borderWarning: bendBaseColors.orange_50,
  borderError: bendBaseColors.error,

  // Icon
  iconDefault: bendBaseColors.gray_60,
  iconActive: bendBaseColors.gray_60,
  iconInactive: bendBaseColors.gray_30,
  iconSuccess: bendBaseColors.green_50,
  iconSuccessAlternate: bendBaseColors.green_50,
  iconWarning: bendBaseColors.orange_50,
  iconError: bendBaseColors.error,
  iconAccent: bendBaseColors.blue_40,
  iconInverse: baseColors.white,

  // Divider
  dividerPrimary: bendBaseColors.gray_20,
  dividerSecondary: bendBaseColors.gray_30,

  // Primary button
  primaryButtonBackgroundDefault: bendBaseColors.yellow_40,
  primaryButtonBackgroundHover: bendBaseColors.yellow_30,
  primaryButtonBackgroundPressed: bendBaseColors.yellow_30,
  primaryButtonBackgroundInactive: baseColors.white,
  primaryButtonBackgroundLoading: bendBaseColors.yellow_40,
  primaryButtonBackgroundFocus: bendBaseColors.yellow_40,
  primaryButtonTextActive: bendBaseColors.gray_60,
  primaryButtonTextInactive: bendBaseColors.gray_30,

  // Aurora's extra primary-button variant uses the Bend primary treatment.
  primaryButtonCobaltLightBackgroundDefault: bendBaseColors.yellow_40,
  primaryButtonCobaltLightBackgroundHover: bendBaseColors.yellow_30,
  primaryButtonCobaltLightBackgroundPressed: bendBaseColors.yellow_30,
  primaryButtonCobaltLightBackgroundInactive: baseColors.white,
  primaryButtonCobaltLightBackgroundLoading: bendBaseColors.yellow_40,
  primaryButtonCobaltLightBackgroundFocus: bendBaseColors.yellow_40,
  primaryButtonCobaltLightTextActive: bendBaseColors.gray_60,
  primaryButtonCobaltLightTextInactive: bendBaseColors.gray_30,

  // Secondary button
  secondaryButtonBackgroundDefault: baseColors.white,
  secondaryButtonBackgroundHover: bendBaseColors.yellow_40,
  secondaryButtonBackgroundPressed: bendBaseColors.yellow_40,
  secondaryButtonBackgroundInactive: baseColors.white,
  secondaryButtonBackgroundFocus: baseColors.white,
  secondaryButtonOutlineDefault: bendBaseColors.gray_60,
  secondaryButtonOutlineInactive: bendBaseColors.gray_20,
  secondaryButtonTextActive: bendBaseColors.gray_60,
  secondaryButtonTextInactive: bendBaseColors.gray_30,

  // Inverse button
  secondaryButtonInverseBackgroundDefault: bendBaseColors.green_50,
  secondaryButtonInverseBackgroundHover: bendBaseColors.green_60,
  secondaryButtonInverseBackgroundPressed: bendBaseColors.green_60,
  secondaryButtonInverseBackgroundFocus: bendBaseColors.green_50,
  secondaryButtonInverseOutlineDefault: baseColors.transparent,
  secondaryButtonInverseOutlinePressed: baseColors.transparent,
  secondaryButtonInverseOutlineInactive: baseColors.transparent,
  secondaryButtonInverseTextActive: baseColors.white,
  secondaryButtonInverseTextInactive: baseColors.white,

  // Tertiary button
  tertiaryButtonBackgroundDefault: baseColors.transparent,
  tertiaryButtonBackgroundHover: bendBaseColors.yellow_40,
  tertiaryButtonBackgroundPressed: bendBaseColors.yellow_40,
  tertiaryButtonBackgroundInactive: baseColors.transparent,
  tertiaryButtonBackgroundFocus: baseColors.transparent,
  tertiaryButtonTextActive: bendBaseColors.gray_60,
  tertiaryButtonTextInactive: bendBaseColors.gray_30,

  // Secondary tertiary/icon button
  secondaryTertiaryButtonBackgroundDefault: bendBaseColors.yellow_40,
  secondaryTertiaryButtonBackgroundHover: bendBaseColors.yellow_10,
  secondaryTertiaryButtonBackgroundPressed: bendBaseColors.yellow_10,
  secondaryTertiaryButtonBackgroundInactive: baseColors.white,
  secondaryTertiaryButtonBackgroundFocus: bendBaseColors.yellow_40,
  secondaryTertiaryButtonTextActive: bendBaseColors.gray_60,
  secondaryTertiaryButtonTextInactive: bendBaseColors.gray_30,
  secondaryIconButtonBackgroundDefault: bendBaseColors.yellow_40,
  secondaryIconButtonBackgroundHover: bendBaseColors.yellow_10,
  secondaryIconButtonBackgroundPressed: bendBaseColors.yellow_10,
  secondaryIconButtonBackgroundInactive: baseColors.white,
  secondaryIconButtonBackgroundFocus: bendBaseColors.yellow_40,
  secondaryIconButtonOutlineDefault: bendBaseColors.gray_60,
  primaryIconButtonBackgroundDefault: baseColors.transparent,
  primaryIconButtonBackgroundHover: bendBaseColors.yellow_40,
  primaryIconButtonBackgroundPressed: bendBaseColors.yellow_40,
  primaryIconButtonBackgroundFocus: baseColors.transparent,
  primaryIconButtonBackgroundInactive: baseColors.transparent,
  primaryIconButtonTextActive: bendBaseColors.gray_60,

  // Word cloud button
  wordCloudButtonBackgroundDefault: baseColors.white,
  wordCloudButtonBackgroundPressed: baseColors.white,
  wordCloudButtonBackgroundSelected: baseColors.teal_10,
  wordCloudButtonOutlineDefault: baseColors.oatmeal_30,
  wordCloudButtonOutlineHover: bendBaseColors.legacy_teal_40,
  wordCloudButtonOutlinePressed: bendBaseColors.legacy_teal_40,
  wordCloudButtonOutlineInactive: baseColors.oatmeal_30,
  wordCloudButtonOutlineSelected: bendBaseColors.legacy_teal_40,
  wordCloudButtonTextActive: baseColors.oatmeal_50,
  wordCloudButtonTextInactive: baseColors.oatmeal_40,
  wordCloudButtonTextPressed: baseColors.teal_50,
  wordCloudButtonTextSelected: baseColors.teal_50,

  // Input field
  inputOutlineDefault: bendBaseColors.gray_20,
  inputOutlineSelected: bendBaseColors.green_50,
  inputOutlineInactive: bendBaseColors.gray_20,
  inputOutlineHover: bendBaseColors.gray_30,
  inputOutlineFocus: bendBaseColors.green_50,
  inputOutlineError: bendBaseColors.error,
  inputTextDefault: bendBaseColors.gray_60,
  inputTextPlaceholder: bendBaseColors.gray_30,
  inputTextInactive: bendBaseColors.gray_30,
  inputTextSuccess: bendBaseColors.green_60,
  inputTextWarning: bendBaseColors.orange_60,
  inputTextError: bendBaseColors.error,
  inputBackgroundDefault: baseColors.white,
  inputBackgroundInactive: bendBaseColors.gray_10,

  // Typeahead/list item
  typeaheadMenuBackground: baseColors.white,
  typeaheadItemBackgroundHover: bendBaseColors.gray_10,

  // Checkbox
  checkboxBackgroundPrimary: baseColors.transparent,
  checkboxBackgroundPrimarySelected: bendBaseColors.green_10,
  checkboxBackgroundSelected: bendBaseColors.green_50,
  checkboxBackgroundPressed: bendBaseColors.green_50,
  checkboxBackgroundSelectedInactive: bendBaseColors.gray_40,
  checkboxOutlineDefault: bendBaseColors.gray_30,
  checkboxOutlineHovered: bendBaseColors.gray_60,
  checkboxOutlineInactive: bendBaseColors.gray_20,
  checkboxOutlineSelected: bendBaseColors.green_50,
  checkboxOutlineSelectedInactive: bendBaseColors.gray_40,
  checkboxCheckSelected: baseColors.white,
  checkboxTextDefault: bendBaseColors.gray_60,
  checkboxTextSelected: bendBaseColors.green_50,

  // Radio
  radioBackgroundPrimary: baseColors.transparent,
  radioBackgroundSelected: baseColors.white,
  radioBackgroundSelectedInactive: baseColors.white,
  radioTextDefault: bendBaseColors.gray_60,
  radioTextHighlight: bendBaseColors.gray_60,
  radioTextSelected: bendBaseColors.gray_60,
  radioOutlineDefault: bendBaseColors.gray_30,
  radioOutlineHover: bendBaseColors.green_50,
  radioOutlineInactive: bendBaseColors.gray_20,
  radioOutlineSelected: bendBaseColors.green_50,
  radioOutlineSelectedInactive: bendBaseColors.gray_40,
  radioOutlineFocused: bendBaseColors.green_50,

  // Deprecated radio with outline
  radioWithOutlineBackgroundHighlight: bendBaseColors.gray_10,
  radioWithOutlinePrimaryTextHighlight: baseColors.teal_50,
  radioWithOutlineBackgroundSelected: baseColors.teal_10,
  radioWithOutlineBorderSelected: bendBaseColors.legacy_teal_40,
  radioWithOutlinePrimaryTextSelected: baseColors.teal_50,
  radioWithOutlineSecondaryTextSelected: bendBaseColors.legacy_teal_40,
  radioWithOutlineBorderHovered: baseColors.teal_30,
  radioWithOutlineBackgroundPressed: baseColors.white,
  radioWithOutlinePrimaryTextInactive: baseColors.oatmeal_40,
  radioWithOutlineSecondaryTextInactive: baseColors.oatmeal_30,
  radioWithOutlineBorderInactive: baseColors.teal_20,
  radioWithOutlineBackgroundDefault: baseColors.white,
  radioWithOutlinePrimaryTextDefault: baseColors.oatmeal_60,
  radioWithOutlineSecondaryTextDefault: baseColors.oatmeal_50,
  radioWithOutlineBorderDefault: baseColors.oatmeal_30,

  // Cards
  cardBackgroundPrimary: baseColors.white,
  cardBackgroundSecondary: bendBaseColors.gray_10_opacity_60,
  cardOutlinePrimary: bendBaseColors.gray_20,
  cardOutlineSecondary: baseColors.transparent,
  cardOutlineInactive: bendBaseColors.gray_10,

  // Progress indicator
  progressIndicator: bendBaseColors.green_50,
  progressIndicatorBackgroundTop: bendBaseColors.gray_10,
  progressIndicatorBackgroundBottom: bendBaseColors.gray_10,
  scrollViewRefresherLoadingIcon: bendBaseColors.green_50,

  // Gradients
  gradientBubbleStart: bendBaseColors.green_10,
  gradientBubbleEnd: bendBaseColors.green_10,
  backgroundGradientFirst: baseColors.white,
  backgroundGradientSecond: baseColors.white,

  // Skeleton
  skeletonBoneColor: bendBaseColors.gray_10,
  skeletonHighlightColor: baseColors.white,

  components: {
    ...auroraLightThemeColors.components,

    badge: {
      ...auroraLightThemeColors.components.badge,
      success: {
        background: bendBaseColors.soft_green_20,
        text: bendBaseColors.gray_60,
      },
      attention: {
        background: bendBaseColors.orange_20,
        text: bendBaseColors.gray_60,
      },
      alert: {
        background: bendBaseColors.red_20,
        text: bendBaseColors.gray_60,
      },
      neutral: {
        background: bendBaseColors.gray_10,
        text: bendBaseColors.gray_60,
      },
      infoPrimary: {
        background: bendBaseColors.teal_20,
        text: bendBaseColors.gray_60,
      },
      infoSecondary: {
        background: bendBaseColors.blue_10,
        text: bendBaseColors.gray_60,
      },
      inverse: {
        background: bendBaseColors.teal_20,
        text: bendBaseColors.gray_60,
      },
      neutralTransparent: {
        background: bendBaseColors.gray_10,
        text: bendBaseColors.gray_60,
      },
    },
    alertBanner: {
      ...auroraLightThemeColors.components.alertBanner,
      danger: { background: baseColors.red_10, text: bendBaseColors.red_60 },
      success: {
        background: bendBaseColors.soft_green_10,
        text: bendBaseColors.green_60,
      },
      warning: {
        background: bendBaseColors.orange_10,
        text: bendBaseColors.orange_60,
      },
      info: {
        background: bendBaseColors.blue_10,
        text: bendBaseColors.blue_60,
      },
      notification: {
        background: baseColors.white,
        text: bendBaseColors.teal_60,
      },
      calm: {
        background: bendBaseColors.blue_10,
        text: bendBaseColors.blue_60,
      },
      neutral: {
        background: bendBaseColors.teal_10,
        text: bendBaseColors.teal_60,
      },
    },
    avatar: {
      ...auroraLightThemeColors.components.avatar,
      tealTheme: {
        background: bendBaseColors.teal_20,
        text: bendBaseColors.gray_60,
      },
      greenTheme: {
        background: bendBaseColors.soft_green_20,
        text: bendBaseColors.gray_60,
      },
      goldTheme: {
        background: bendBaseColors.yellow_20,
        text: bendBaseColors.gray_60,
      },
      peachTheme: {
        background: bendBaseColors.orange_20,
        text: bendBaseColors.gray_60,
      },
      periwinkleTheme: {
        background: bendBaseColors.purple_20,
        text: bendBaseColors.gray_60,
      },
      defaultAvatarTextColor: bendBaseColors.gray_60,
    },
    chatBubble: {
      ...auroraLightThemeColors.components.chatBubble,
      background: bendBaseColors.green_10,
      border: baseColors.transparent,
    },
    messageItem: {
      ...auroraLightThemeColors.components.messageItem,
      chatBubbleReceiver: {
        background: bendBaseColors.green_10,
        border: baseColors.transparent,
      },
      chatBubbleSender: {
        background: bendBaseColors.green_10,
        border: baseColors.transparent,
      },
    },
    progressBar: {
      fill: bendBaseColors.green_50,
      background: bendBaseColors.gray_10,
    },
    progressBarSecondary: {
      fill: bendBaseColors.green_50,
      background: baseColors.white,
    },
    sliderField: {
      ...auroraLightThemeColors.components.sliderField,
      text: bendBaseColors.gray_60,
      thumb: {
        ...auroraLightThemeColors.components.sliderField.thumb,
        background: baseColors.white,
        border: bendBaseColors.green_50,
      },
      track: {
        ...auroraLightThemeColors.components.sliderField.track,
        filled: { background: bendBaseColors.green_50 },
        unFilled: { background: bendBaseColors.gray_10 },
        minimumTint: { background: bendBaseColors.green_50 },
        maximumTint: { background: bendBaseColors.gray_10 },
      },
      stepMarker: {
        ...auroraLightThemeColors.components.sliderField.stepMarker,
        filled: { background: baseColors.white },
        unFilled: { background: bendBaseColors.gray_20 },
        active: { background: baseColors.white },
      },
      floatingLabel: {
        ...auroraLightThemeColors.components.sliderField.floatingLabel,
        background: baseColors.white,
        border: bendBaseColors.green_50,
      },
    },
    multicolorSliderField: {
      ...auroraLightThemeColors.components.multicolorSliderField,
      text: bendBaseColors.gray_60,
      thumb: {
        ...auroraLightThemeColors.components.multicolorSliderField.thumb,
        background: baseColors.white,
        border: bendBaseColors.green_50,
      },
      track: {
        ...auroraLightThemeColors.components.multicolorSliderField.track,
        filled: { background: bendBaseColors.green_50 },
        unFilled: { background: bendBaseColors.gray_10 },
        minimumTint: { background: bendBaseColors.green_50 },
        maximumTint: { background: bendBaseColors.gray_10 },
      },
      stepMarker: {
        ...auroraLightThemeColors.components.multicolorSliderField.stepMarker,
        filled: { background: baseColors.white },
        unFilled: { background: bendBaseColors.gray_20 },
        active: { background: baseColors.white },
      },
      floatingLabel: {
        ...auroraLightThemeColors.components.multicolorSliderField.floatingLabel,
        background: baseColors.white,
        border: bendBaseColors.green_50,
      },
    },
    floatingMobileNav: {
      background: baseColors.white,
      iconActive: bendBaseColors.green_50,
      iconDefault: bendBaseColors.gray_60,
      activeIndicator: bendBaseColors.green_50,
    },
  },
  icons: {
    ...auroraLightThemeColors.icons,

    lyraLogoPrimaryIcon: {
      fill: bendBaseColors.legacy_teal_40,
    },
    spotIllustration: {
      ...auroraLightThemeColors.icons.spotIllustration,
      primary: { fill: bendBaseColors.teal_30 },
      secondary: { fill: bendBaseColors.teal_40 },
      background: { fill: bendBaseColors.green_10 },
    },
    benefitIllustration: {
      ...auroraLightThemeColors.icons.benefitIllustration,
      primary: { fill: bendBaseColors.teal_30 },
      secondary: { fill: bendBaseColors.teal_40 },
    },
  },
}
